import _ from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import Stack from 'molecules/stack/stack';
import StackItem from 'molecules/stackItem/stackItem';
import Icon from 'atoms/icons/icon';
import RenderInput from './renderInput';
import HealthCheck from 'pages/orders/healthCheck/healthCheck';
import DatePicker from 'atoms/datePicker/datePicker';
import { useDispatch, useSelector } from 'react-redux';
import * as dynamicGridSagaActions from '../dynamicGrid/sagaActions';
import moment, { Moment } from 'moment';
import { Checkbox, Tooltip, Tag } from 'antd';
import Link from 'atoms/link/link';
import { formatPercentage, formatMoney, formatThousands, formatDate } from 'utils/utilities';
import Validation from 'atoms/validation/validation';
import { getColumnInfoByIndentifier } from './selector';
import { getGridMetadata } from 'models/dynamicGrid/selector';
import { usePopupParent } from 'hooks/usePopupParent';
import { RenderProps } from './types';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
// Function for formatting the value based on the type
const formatValueByType = (value: any, type: string, tableId: string | number): string | null | Moment => {
	switch (type) {
		case 'boolean':
			return JSON.stringify(value);
		case 'price':
			return formatMoney(value, true);
		case 'qty':
			return formatThousands(value);
		case 'percentage':
			return formatPercentage(value);
		case 'percentagefull':
			return formatPercentage(value, true);
		case 'date':
			return formatDate(value);
		case 'number':
			if (typeof value === 'number' && !isNaN(value)) {
				if (tableId === 33) {
					return value % 1 !== 0 ? value.toFixed(2) : value.toString();
				}
				return value % 1 !== 0 ? value.toFixed(1) : value.toString();
			}
			return value;
		case 'text':
		default:
			return value;
	}
};

function useChildGridSync(params: any) {
	const { data, tableId, api, node } = params;

	const uniqueId = 'OrderDetailVarietyID';
	const qtyKey = 'ItemOrderQty';

	useEffect(() => {
		if (!data || !data[qtyKey]) return;
		if (![3, 23].includes(tableId)) {
			return;
		}

		const sync = () => {
			let errors: any[] = [];
			let success: any[] = [];

			try {
				const rawError = localStorage.getItem('rowUpdateError');
				const rawSuccess = localStorage.getItem('rowUpdateSuccess');

				if (rawError) errors = JSON.parse(rawError);
				if (rawSuccess) success = JSON.parse(rawSuccess);
			} catch {
				return;
			}

			const errMatch = errors.find((e) => String(e.RowIdentifier) === String(data[uniqueId]));
			const successMatch = success.find((e) => String(e.RowIdentifier) === String(data[uniqueId]));

			if (!errMatch && !successMatch) return;

			const originalValue = data.ProjectedOnHand;

			// Ensure we handle both object and primitive values
			const currentQtyValue = data[qtyKey];
			const isCurrentObject =
				currentQtyValue && typeof currentQtyValue === 'object' && !Array.isArray(currentQtyValue);

			data[qtyKey] = {
				...(isCurrentObject ? currentQtyValue : { value: currentQtyValue }),
				...(successMatch && {
					value: successMatch[qtyKey],
					isUpdated: true,
				}),
				...(errMatch && {
					value: errMatch[qtyKey],
					hasError: true,
					errorMessage: errMatch.ErrorMessage,
				}),
			};

			api.refreshCells({
				rowNodes: [node],
				columns: [qtyKey],
				force: true,
			});

			if (errMatch) {
				setTimeout(() => {
					data[qtyKey] = {
						...data[qtyKey],
						value: originalValue,
						hasError: false,
						errorMessage: null,
					};

					localStorage.removeItem('rowUpdateError');

					api.refreshCells({
						rowNodes: [node],
						columns: [qtyKey],
						force: true,
					});
				}, 5000);
			}

			if (successMatch) {
				setTimeout(() => {
					// Ensure data[qtyKey] is an object before setting properties
					const currentQtyValue = data[qtyKey];
					const isCurrentObject =
						currentQtyValue && typeof currentQtyValue === 'object' && !Array.isArray(currentQtyValue);
					if (isCurrentObject) {
						data[qtyKey].isUpdated = false;
					} else {
						// If it's not an object, preserve the value and create an object
						data[qtyKey] = {
							value: currentQtyValue,
							isUpdated: false,
						};
					}
					localStorage.removeItem('rowUpdateSuccess');

					api.refreshCells({
						rowNodes: [node],
						columns: [qtyKey],
						force: true,
					});
				}, 5000);
			}
		};

		window.addEventListener('localStorageChange', sync);
		sync();

		return () => {
			window.removeEventListener('localStorageChange', sync);
		};
	}, [data, api, node, qtyKey, uniqueId]);
}

function useSyncErrorFromLocalStorage(params: any) {
	const { data, tableId, api, node } = params;

	const uniqueId = tableId === 1 ? 'OrderDetailID' : 'OrderDetailVarietyID';
	const qtyKey = tableId === 1 ? 'OrderQty' : 'ItemOrderQty';
	const rawError = localStorage.getItem('rowUpdateError');
	const rawSuccess = localStorage.getItem('rowUpdateSuccess');

	useEffect(() => {
		if (!data) return;

		const allowedTables = [1, 2];
		if (!allowedTables.includes(tableId)) return;

		let errors = [];
		let success = [];
		try {
			if (rawError) errors = JSON.parse(rawError);
			if (rawSuccess) success = JSON.parse(rawSuccess);
		} catch {
			return;
		}
		// ---------- ERROR MATCH ----------
		const errMatch = errors.find((e: any) => String(e.RowIdentifier) === String(data[uniqueId]));

		const successMatch = success.find((e: any) => String(e.RowIdentifier) === String(data[uniqueId]));
		if (!errMatch && !successMatch) return;

		const originalValue = tableId === 2 ? data.OrderQtyEaches : data.ProjectedOnHand;

		// Ensure we handle both object and primitive values
		const currentQtyValue = data[qtyKey];
		const isCurrentObject =
			currentQtyValue && typeof currentQtyValue === 'object' && !Array.isArray(currentQtyValue);

		data[qtyKey] = {
			...(isCurrentObject ? currentQtyValue : { value: currentQtyValue }),
			...(successMatch && {
				value: successMatch[qtyKey],
				isUpdated: true,
			}),
			...(errMatch && {
				value: errMatch[qtyKey],
				hasError: true,
				errorMessage: errMatch.ErrorMessage,
			}),
		};
		if (errMatch) {
			setTimeout(() => {
				data[qtyKey] = {
					...data[qtyKey],
					value: originalValue,
					hasError: false,
					errorMessage: null,
				};

				localStorage.removeItem('rowUpdateError');

				api.refreshCells({
					rowNodes: [node],
					columns: [qtyKey],
					force: true,
				});
			}, 5000);
		}

		if (successMatch) {
			setTimeout(() => {
				const currentValue = data[qtyKey];

				if (currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)) {
					// already an object → safe
					data[qtyKey].isUpdated = false;
				} else {
					// string / number / null → convert to object
					data[qtyKey] = {
						value: currentValue,
						isUpdated: false,
					};
				}

				localStorage.removeItem('rowUpdateSuccess');
			}, 5000);
		}
	}, [data, rawError, rawSuccess, tableId, qtyKey, uniqueId]);
}

const RenderCellWithErrorSync = (params: any) => {
	useChildGridSync(params);
	useSyncErrorFromLocalStorage(params);
	return <RenderCellAg {...params} />;
};

export const createComponent = (componentName: string, params: any) => {
	switch (componentName) {
		case 'RenderTiles':
			return <RenderTiles row={params.row || params.data} {...params} />;
		case 'RenderBooleanStatus':
			return <RenderBooleanStatus {...params} />;
		case 'RenderCell':
			return <RenderCellWithErrorSync {...params} />;
		case 'RenderIcon':
			return <RenderIcon row={params.row || params.data} rowIdentifier={params.rowIdentifier} {...params} />;
		case 'RenderInput':
			return <RenderCellWithErrorSync {...params} />;
		case 'RenderCheckbox':
			return (
				<RenderCheckBox
					{...params}
					isPivotMode={params?.isPivotMode}
					row={params.row || params.data}
					rowIdentifier={params.isPivotViewEnabled ? params.colDef.colId : params.rowIdentifier}
					tableId={params.tableId}
				/>
			);

		case 'RenderHealthCheck':
			return <RenderHealthCheck {...params} healthCheckId={params.rowIdentifier} />;
		case 'RenderLock':
			return <RenderLock row={params.row || params.data} rowIdentifier={params.rowIdentifier} {...params} />;
		case 'RenderDate':
			return (
				<RenderDate
					row={params.row || params.data}
					rowIdentifier={params.rowIdentifier}
					tableId={params.tableId}
					{...params}
				/>
			);
		case 'RenderLink':
			return (
				<RenderLink
					row={params.row || params.data}
					rowIdentifier={params.rowIdentifier}
					tableId={params.tableId}
					{...params}
				/>
			);
		default:
			return <React.Fragment />;
	}
};

const getHealthCheckType = ({ isAlert, IsWarn }: { isAlert: string; IsWarn: string }) => {
	if (isAlert) {
		return 'alert';
	}
	if (IsWarn) {
		return 'warning';
	}
};

export const RenderHealthCheck = (props: any): JSX.Element => {
	const { data: row, colDef, node, defaultRowGroupColumnDisplayOverride, value } = props;
	let rowIdentifier;

	// Determine rowIdentifier and tableId based on the column definition and row group
	if (colDef.showRowGroup) {
		rowIdentifier = defaultRowGroupColumnDisplayOverride || colDef.showRowGroup || node.field;
	} else {
		rowIdentifier = colDef.field;
	}
	const [isHelthCheckEnabled, handleHealthCheck] = useState(false);
	if (_.isNil(row.HealthCheckID)) {
		// If both row[rowIdentifier] and value are null or undefined, return an empty React.Fragment
		return <React.Fragment />;
	} else {
		// If at least one of row[rowIdentifier] or value is not null or undefined, render the components
		return (
			<Stack align="left">
				<StackItem position="center" align="center" isGrow>
					<Icon
						faAlias="faHeartbeat"
						type={getHealthCheckType(row)}
						onClick={() => handleHealthCheck(true)}
						isActive={true}
					></Icon>
				</StackItem>
				{isHelthCheckEnabled && (
					<HealthCheck
						handleOnClose={() => handleHealthCheck(false)}
						healthCheckId={row ? row[rowIdentifier] : value || 0}
					></HealthCheck>
				)}
			</Stack>
		);
	}
};

const getAlignment = (type: string) => {
	switch (type?.toLowerCase()) {
		case 'number':
			return 'right';
		case 'price':
			return 'right';
		case 'qty':
			return 'right';
		case 'percentage':
			return 'right';
		case 'percentagefull':
			return 'right';
		case 'text':
		default:
			return 'left';
	}
};
export const RenderUpdateText = (props: any): JSX.Element => {
	const { row, rowIdentifier, type } = props;
	if (row.isEditEnabled) {
		return (
			<RenderInput
				isUploadGrid={props?.isUploadGrid}
				isInlineEdit
				type={type}
				row={row}
				rowIdentifier={rowIdentifier}
			/>
		);
	}
	return <RenderCell {...props} />;
};

export const RenderCellAg = (props: RenderProps): JSX.Element => {
	const { data: row, colDef, node, isUploadGrid, defaultRowGroupColumnDisplayOverride, value, tableId, cellRendererFramework } = props;
	const getPopupParent = usePopupParent('full-screen');

	let rowIdentifier: string = props.rowIdentifier;

	if (colDef.showRowGroup) {
		rowIdentifier = (defaultRowGroupColumnDisplayOverride || node.field) ?? rowIdentifier;
	} else {
		rowIdentifier = colDef.field ?? rowIdentifier;
	}

	const column = useSelector((state) => getColumnInfoByIndentifier(state, tableId, rowIdentifier));
	const type = column?.ColumnType ?? props.type;

	// console.log(row[rowIdentifier],"row?.[rowIdentifier]242")
	// Get the value of the cell, applying necessary formatting
	// IMPORTANT: Extract value but preserve the object structure for validation checks
	const cellValue = row
		? row?.[rowIdentifier]?.hasOwnProperty('value')
			? row[rowIdentifier].value
			: row[rowIdentifier]
		: value;
	// Apply the formatting based on the type using the new formatValueByType function
	const formattedValue =
		cellValue === 'null' || _.isNil(cellValue) ? '' : formatValueByType(cellValue, type, tableId);

	// Format the group row differently, appending the child count if applicable
	const isGroupNode = node?.group;
	const childRowCount = row?.['ChildRowCount'];
	const displayValue = !formattedValue
		? ''
		: isGroupNode && childRowCount
			? `${formattedValue} (${childRowCount})`
			: formattedValue;

	// Render the input field for editable columns
	if (row?.isEditEnabled && column?.IsEditableColumn && !isGroupNode) {
		return (
			<RenderInput
				isUploadGrid={isUploadGrid}
				isInlineEdit
				align={type ?? getAlignment(type)}
				type={type}
				row={row}
				rowIdentifier={rowIdentifier}
				tableId={tableId}
			/>
		);
	}

	// Determine the component to render based on the type
	// Note: cellRendererFramework may be an async function (e.g. handleModulePermission in Roles).
	// Only checkbox columns should use it — they await it in an event handler (RenderCheckBox).
	// Calling it inline here would return a Promise, crashing React with error #31.
	let component = (
		<Stack
			key={row ? `${rowIdentifier}-${row[rowIdentifier]}` : `${rowIdentifier}-${value}`}
			align={getAlignment(type)}
		>
			<StackItem isGrow isEllipsisFull>
				<Tooltip getPopupContainer={getPopupParent} title={displayValue}>
					<span className="grid-row--text">{displayValue}</span>
				</Tooltip>
			</StackItem>
		</Stack>
	);

	if (type === 'bit') {
		component = (
			<Stack align="left">
				<StackItem isGrow align="center" position="center" isEllipsisFull>
					<span className="grid-row--text">
						{displayValue && <Icon type="active" isActive faAlias="faCheck" />}
					</span>
				</StackItem>
			</Stack>
		);
	}

	// Custom date and text handling
	if (type === 'date') {
		component = (
			<Tooltip getPopupContainer={getPopupParent} key={`${displayValue}`} title={displayValue}>
				<span className="grid-row--text">{displayValue}</span>
			</Tooltip>
		);
	}
	// console.log(row[rowIdentifier],"row?.[rowIdentifier]290",row);
	// Check for validation state - row[rowIdentifier] should be an object with hasError/isUpdated
	const cellData = row?.[rowIdentifier];
	// Only check for validation properties if cellData is an object (not a string/number)
	const isCellDataObject = cellData && typeof cellData === 'object' && !Array.isArray(cellData);
	const hasError = isCellDataObject ? cellData?.hasError : false;
	const hasWarning = isCellDataObject ? cellData?.hasWarning : false;
	const isUpdated = isCellDataObject ? cellData?.isUpdated : false;

	const isOrderQtyField =
		rowIdentifier === 'OrderQty' || rowIdentifier === 'ItemOrderQty' || rowIdentifier?.includes('|ItemOrderQty');

	if (row && (hasError || hasWarning || isUpdated)) {
		return (
			<Validation
				key={`${rowIdentifier}-${row[rowIdentifier]} -${node.rowIndex}`}
				row={row}
				rowIdentifier={rowIdentifier}
				tableId={tableId}
				{...(_.isString(row[rowIdentifier]) ? {} : row[rowIdentifier])}
			>
				<div
					className={`grid-text
  ${hasError || hasWarning ? 'border-error' : ''}
  ${isUpdated && !hasError && isOrderQtyField ? 'border-updated-blue' : ''}
`}
				>
					{component}
				</div>
			</Validation>
		);
	}

	return component;
};

export const RenderCell = (props: any): JSX.Element => {
	const { row, rowIdentifier, type, tableId } = props;
	const getPopupParent = usePopupParent('full-screen');
	const column = useSelector((state) => getColumnInfoByIndentifier(state, tableId, rowIdentifier));
	if (row.isEditEnabled && column?.IsEditableColumn) {
		return (
			<RenderInput
				isInlineEdit
				isUploadGrid={props.isUploadGrid}
				align={getAlignment(type)}
				type={type}
				row={row}
				rowIdentifier={rowIdentifier}
				tableId={tableId}
			/>
		);
	}

	const getValue = () => {
		switch (type) {
			case 'price':
				return (
					formatMoney(
						row[rowIdentifier]?.hasOwnProperty('value') ? row[rowIdentifier].value : row[rowIdentifier],
						true
					) || 0
				);
			case 'qty':
				return (
					formatThousands(
						row[rowIdentifier]?.hasOwnProperty('value') ? row[rowIdentifier].value : row[rowIdentifier]
					) || 0
				);
			case 'percentage':
				return formatPercentage(row[rowIdentifier]);
			case 'percentagefull':
				return formatPercentage(row[rowIdentifier], true);
			case 'date':
				return row[rowIdentifier]?.hasOwnProperty('value')
					? row[rowIdentifier].value
					: row[rowIdentifier]
						? formatDate(
								row[rowIdentifier]?.hasOwnProperty('value')
									? row[rowIdentifier].value
									: row[rowIdentifier]
							)
						: '-';
			case 'number':
			case 'text':
			default:
				return row[rowIdentifier]?.hasOwnProperty('value') ? row[rowIdentifier].value : row[rowIdentifier];
		}
	};

	let component = (
		<Stack key={`${rowIdentifier}-${row[rowIdentifier]}`} align={getAlignment(type)}>
			<StackItem position="center" isGrow isEllipsisFull>
				{/* {( */}
				<Tooltip getPopupContainer={getPopupParent} title={getValue()}>
					{getValue()}{' '}
				</Tooltip>
				{/* ) || (
          <span className="grid-row--text">
            {row[rowIdentifier]?.hasOwnProperty("value")
              ? row[rowIdentifier].value
              : row[rowIdentifier]}
          </span>
        )} */}
			</StackItem>
		</Stack>
	);
	if (type === 'text') {
		component = (
			<Stack key={`${rowIdentifier}-${row[rowIdentifier]}`} align={getAlignment(type)}>
				<StackItem position="center" isGrow isEllipsisFull>
					<Tooltip
						getPopupContainer={getPopupParent}
						title={
							row[rowIdentifier]?.hasOwnProperty('value') ? row[rowIdentifier].value : row[rowIdentifier]
						}
					>
						<span className="grid-row--text">
							{row[rowIdentifier]?.hasOwnProperty('value')
								? row[rowIdentifier].value
								: row[rowIdentifier]}
						</span>
					</Tooltip>
				</StackItem>
			</Stack>
		);
	}

	if (type === 'date') {
		component = (
			<Tooltip
				getPopupContainer={getPopupParent}
				key={`${rowIdentifier}-${row[rowIdentifier]}`}
				title={getValue()}
			>
				<span className="grid-row--text">{getValue()} </span>
			</Tooltip>
		);
	}

	if (row[rowIdentifier]?.hasError || row[rowIdentifier]?.hasWarning) {
		return (
			<Validation
				row={row}
				rowIdentifier={rowIdentifier}
				tableId={tableId}
				{...(_.isString(row[rowIdentifier]) ? {} : row[rowIdentifier])}
			>
				{component}
			</Validation>
		);
	}

	return component;
};

export const RenderIcon = (props: RenderProps): JSX.Element => {
	const { data: row, colDef, node, defaultRowGroupColumnDisplayOverride } = props;
	let rowIdentifier: string;

	// Determine rowIdentifier and tableId based on the column definition and row group
	if (colDef.showRowGroup) {
		rowIdentifier = node.field ?? '';
		// (defaultRowGroupColumnDisplayOverride || colDef.showRowGroup || node.field) ?? "";
	} else {
		rowIdentifier = colDef.field ?? '';
	}

	const isGroupNode = node?.group;
	const childRowCount = row?.['ChildRowCount'];

	let faIcon: string;

	switch (rowIdentifier) {
		default:
			faIcon = 'faCheck';
	}

	return (
		<Stack align="left">
			<StackItem>{row[rowIdentifier] && <Icon isActive type="secondary" faAlias={faIcon} />}</StackItem>
			{isGroupNode && childRowCount && <StackItem>({childRowCount})</StackItem>}
		</Stack>
	);
};

export const RenderTiles = (props: RenderProps): JSX.Element => {
	const { data: row, colDef, node, defaultRowGroupColumnDisplayOverride } = props;
	const getPopupParent = usePopupParent('full-screen');

	let rowIdentifier: string;

	// Determine rowIdentifier and tableId based on the column definition and row group
	if (colDef.showRowGroup) {
		rowIdentifier = node.field ?? '';
		// (defaultRowGroupColumnDisplayOverride || colDef.showRowGroup || node.field) ?? "";
	} else {
		rowIdentifier = colDef.field ?? '';
	}

	const isGroupNode = node?.group;
	const childRowCount = row?.['ChildRowCount'];

	const showValue = () => {
		const icons = _.map(row?.[rowIdentifier] ?? [], (r) => r?.Value);
		const text = icons.join();
		return text;
	};

	return (
		<Tooltip getPopupContainer={getPopupParent} key={`${rowIdentifier}-${row[rowIdentifier]}`} title={showValue()}>
			<span className="grid-row--text">
				<Stack align="left" gutter="small" position="center">
					<StackItem isGrow>
						{_.map(row?.[rowIdentifier] ?? [], (r) => (
							<Tag>{r?.Value}</Tag>
						))}
					</StackItem>
					{isGroupNode && childRowCount && <StackItem>({childRowCount})</StackItem>}
				</Stack>
			</span>
		</Tooltip>
	);
};

export const RenderLock = (props: RenderProps): JSX.Element => {
	const { data: row, value, rowIdentifier, node } = props;

	const isGroupNode = node?.group;
	const childRowCount = row?.['ChildRowCount'];

	if (!row?.[rowIdentifier] && !value) {
		return <> </>;
	}
	return (
		<Stack align="left" gutter="small" isWrap>
			<StackItem>
				<Icon faAlias="faLock" />
			</StackItem>
			{isGroupNode && childRowCount && (
				<StackItem>
					<span className="grid-row--text">({childRowCount})</span>
				</StackItem>
			)}
		</Stack>
	);
};

const RenderDate = (props: RenderProps): JSX.Element => {
	const { data: row, node, value, colDef, defaultRowGroupColumnDisplayOverride } = props;
	const tableId = Number(props.tableId);
	const getPopupParent = usePopupParent('full-screen');
	const gridMetaData = useSelector((state) => getGridMetadata(state, tableId));
	const isGroupNode = Boolean(node?.group);
	let rowIdentifier: string;

	// Determine rowIdentifier and tableId based on the column definition and row group
	if (colDef.showRowGroup) {
		rowIdentifier = node.field ?? '';
		// (defaultRowGroupColumnDisplayOverride || colDef.showRowGroup || node.field) ?? "";
	} else {
		rowIdentifier = colDef.field ?? '';
	}
	const dispatch = useDispatch();

	// Get the column definition and type for the current column
	const column = useSelector((state) => getColumnInfoByIndentifier(state, tableId, rowIdentifier));

	// Function to handle updating the value when inline editing
	const handleUpdateValue = (updatedValue: string) => {
		dispatch(
			dynamicGridSagaActions.updateRowData({
				tableId: Number(tableId),
				row,
				rowIdentifier,
				updatedValue: new Date(updatedValue),
				isInlineEdit: true,
				isGroupNode,
				metaData: gridMetaData,
			})
		);
	};

	// Get the raw value for the row
	const rawValue = row ? row[rowIdentifier] : value;

	// Format the raw value, if necessary
	const formattedValue = rawValue ? formatDate(rawValue) : '-';

	// Handle grouping logic (if the node is a group)
	const childRowCount = row?.['ChildRowCount'];
	const displayValue = !formattedValue
		? ''
		: isGroupNode && childRowCount
			? `${formattedValue} (${childRowCount})`
			: formattedValue;

	// Inline edit logic for editable columns
	if (!isGroupNode && row?.isEditEnabled && column?.IsEditableColumn) {
		return (
			<DatePicker
				// getPopupContainer={(triggerNode?: HTMLElement | undefined) => getPopupParent(triggerNode)}
				name={rowIdentifier}
				selectedDate={row[rowIdentifier]}
				handleClick={(e: any) => {
					const newValue = (e.target as HTMLInputElement).value;
					handleUpdateValue(newValue);
				}}
			/>
		);
	}

	// Return the formatted date for non-editable cells or group rows
	return (
		<Stack>
			<StackItem>
				<span className="grid-row--text">{displayValue}</span>
			</StackItem>
		</Stack>
	);
};

export const RenderLink = (props: RenderProps): JSX.Element => {
	const { data: row, colDef, node, defaultRowGroupColumnDisplayOverride, value, tableId } = props;
	let rowIdentifier: string;

	// Determine rowIdentifier and tableId based on the column definition and row group
	if (colDef.showRowGroup) {
		rowIdentifier = node.field ?? '';
	} else {
		rowIdentifier = colDef.field ?? '';
	}

	// Get the column definition and type
	const column = useSelector((state) => getColumnInfoByIndentifier(state, tableId, rowIdentifier));
	const type = column?.ColumnType ?? '';
	const cellValue = row
		? row?.[rowIdentifier]?.hasOwnProperty('value')
			? row[rowIdentifier].value
			: row[rowIdentifier]
		: value;
	// Get the raw value from the row data
	const rawValue = row ? row[rowIdentifier] : value;
	const isGroupNode = props.node?.group;
	const formattedValue =
		cellValue === 'null' || _.isNil(cellValue) ? '' : formatValueByType(cellValue, type, tableId);
	// Check if the node is a group and get the child row count
	const childRowCount = row?.['ChildRowCount'];
	const displayValue = !formattedValue
		? ''
		: isGroupNode && childRowCount
			? `${formattedValue} (${childRowCount})`
			: formattedValue;

	// Render the link for tableId 23 with group handling
	if (tableId === 23) {
		return (
			<Stack>
				<StackItem>
					<Link type="tertiary" route={`/orders/details/${rawValue}`}>
						{displayValue}
					</Link>
				</StackItem>
			</Stack>
		);
	}

	// Default rendering for other tableId values, use RenderCell
	return <RenderCell {...props} />;
};

export const RenderBooleanStatus = (props: RenderProps) => {
	const { data: row, colDef, node, defaultRowGroupColumnDisplayOverride, value } = props;
	let rowIdentifier: string;

	// Determine rowIdentifier and tableId based on the column definition and row group
	if (colDef.showRowGroup) {
		rowIdentifier = node.field ?? '';
	} else {
		rowIdentifier = colDef.field ?? '';
	}

	const cellValue = row ? row[rowIdentifier] : value;
	const formattedValue = cellValue ? 'Active' : 'Inactive';

	// Format the group row differently, appending the child count if applicable
	const isGroupNode = node?.group;
	const childRowCount = row?.['ChildRowCount'];
	const displayValue = isGroupNode && childRowCount ? `${formattedValue} (${childRowCount})` : formattedValue;
	return <span>{displayValue}</span>;
};

export const RenderCheckBox = (props: RenderProps) => {
	const dispatch = useDispatch();

	const { row, rowIdentifier, isPaginated = false, params, cellRendererFramework } = props;
	const isUploadGrid = Boolean(props.isUploadGrid);
	const tableId = Number(props.tableId);

	const gridMetaData = useSelector((state: any) => getGridMetadata(state, tableId));

	const value: boolean = row?.[rowIdentifier]?.hasOwnProperty('value')
		? row[rowIdentifier].value
		: row?.[rowIdentifier];

	const [inputValue, updateInputValue] = useState<boolean>(value);

	const handleUpdate = (parsedValue: boolean) => {
		updateInputValue(parsedValue);
		dispatch(
			dynamicGridSagaActions.updateRowData({
				tableId,
				row,
				rowIdentifier,
				isUploadGrid,
				updatedValue: parsedValue,
				isInlineEdit: true,
				isGroupNode: params?.node?.group,
				metaData: gridMetaData,
			})
		);
	};

	// Sync inputValue when row data changes
	useEffect(() => {
		const updatedValue: boolean = row?.[rowIdentifier]?.hasOwnProperty('value')
			? row[rowIdentifier].value
			: row?.[rowIdentifier];
		updateInputValue(updatedValue);
	}, [row?.[rowIdentifier]]);

	if (row?.IsParent === 0 || !row.hasOwnProperty(rowIdentifier)) {
		return <RenderCell row={row} rowIdentifier={rowIdentifier} type="number" />;
	}

	const handleChange = async (e: CheckboxChangeEvent) => {
		const checked = e.target.checked;
		if (cellRendererFramework) {
			const result = await cellRendererFramework({
				...props,
				value: e.target.checked,
			});
			if (result.status === 200) {
				updateInputValue(checked);
				e.stopPropagation();
			}
		} else {
			handleUpdate(checked);
			e.stopPropagation();
		}
	};

	const restrictedWorkflows = ['LOWES_FORECAST', 'INTELLIGENCE', 'MATRIX', 'AIM', 'REPORTING', 'ORDER_WRITING'];
	const isRestrictedWorkflow = restrictedWorkflows.includes(row?.WorkflowName);

	const hasGridEditOperation = gridMetaData?.dataProviderOptions?.isGridEditDisabledOverride;
	const isLocked =
		row?.IsReadOnly ||
		row?.OrderStatusID === 2 ||
		hasGridEditOperation ||
		(isRestrictedWorkflow && (rowIdentifier === 'IsWrite' || rowIdentifier === 'IsDelete')) ||
		false;

	return (
		<div className="render-input-right">
			<Validation row={row} rowIdentifier={rowIdentifier} {...row[rowIdentifier]}>
				<Checkbox checked={inputValue} onChange={handleChange} disabled={isLocked} />
			</Validation>
		</div>
	);
};
